import { Honeybadger, HoneybadgerErrorBoundary } from "@honeybadger-io/react"
import React from "react"

import { isForeignScriptError, shouldIgnoreHoneybadgerNoise } from "../utils/honeybadgerNoiseFilter"

/** In test env, show the real error message so specs fail with a useful message instead of "An Error Occurred" */
const TestErrorFallback = ({ error, info }) => (
  <div className="error" data-testid="error-boundary-fallback">
    <div>An Error Occurred</div>
    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{error?.message || String(error)}</pre>
    {error?.stack && (
      <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 200, overflow: "auto" }}>{error.stack}</pre>
    )}
    {info?.componentStack && (
      <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 150, overflow: "auto" }}>
        {info.componentStack}
      </pre>
    )}
  </div>
)

// API key must come from ENV via window.App.honeybadger_js_api_key (set in shared/_global_vars from HONEYBADGER_JS_API_KEY).
const honeybadger = Honeybadger.configure({
  apiKey: typeof window !== "undefined" ? window.App?.honeybadger_js_api_key : undefined,
  environment: window.App?.environment,
  projectRoot: "webpack:///./app/javascript",
  revision: window.App?.heroku_slug_commit,
  enableUncaught: true,
  beforeNotify: (notice) => {
    // Correlation: request_id from meta tag or last GraphQL response (see utils.jsx graphqlFetch).
    const requestId =
      typeof document !== "undefined"
        ? document.querySelector('meta[name="request-id"]')?.getAttribute("content") || window.__lastGraphqlRequestId
        : null
    if (requestId) {
      notice.context = { ...notice.context, request_id: requestId }
    }
    // Context for debugging (no PII): user_id and practice_id from layout (shared/_global_vars).
    const userId = typeof window !== "undefined" ? window.App?.user_id : null
    const practiceId = typeof window !== "undefined" ? window.App?.practice_id : null
    if (userId || practiceId) {
      notice.context = {
        ...notice.context,
        ...(userId && { user_id: userId }),
        ...(practiceId && { practice_id: practiceId })
      }
    }

    const msg = notice.message || ""
    const stack = notice.stack || ""
    const name = notice.name || ""

    // --- Ignore rules (known-noisy or non-actionable). Document new rules in comments. ---

    // lastSetTimerId: third-party scripts (VWO, extensions) vs Honeybadger timer wrappers.
    // Chrome: "Cannot set properties of undefined (setting 'lastSetTimerId')"
    // Safari: "undefined is not an object (evaluating 'this.lastSetTimerId=null')"
    // Includes minified "t is not a function" + <anonymous> onerror (HB 133309416).
    if (shouldIgnoreHoneybadgerNoise({ message: msg, name, stack, backtrace: notice.backtrace })) return false

    // ResizeObserver loop — benign browser quirk, not actionable.
    if (msg.includes("ResizeObserver loop")) return false

    // NotFoundError: removeChild — third-party scripts (VWO, browser extensions) mutate
    // the DOM concurrently with React, causing stale-node removal failures.
    if (msg.includes("removeChild") && msg.includes("not a child of this node")) return false

    // SecurityError — cross-origin restriction on custom-domain iframes / cookie access (Safari).
    if (name === "SecurityError" || msg === "The operation is insecure.") return false

    // NetworkError from third-party tracking pixels (Pinterest, LinkedIn, etc.).
    if (
      name === "NetworkError" &&
      (stack.includes("pinimg.com") ||
        stack.includes("pinterest.com") ||
        stack.includes("linkedin.com") ||
        stack.includes("google.com") ||
        stack.includes("facebook"))
    )
      return false

    // FullCalendar: @font-face (fcicons) via insertRule — Chrome throws DOMException; benign after safeCssInsertRulePatch.
    if (
      (name === "DOMException" || name === "SyntaxError") &&
      (msg.includes("insertRule") ||
        msg.includes("CSSStyleSheet") ||
        msg.includes("fcicons") ||
        msg.includes("@font-face"))
    )
      return false

    // SyntaxError: Invalid or unexpected token — injected third-party snippet parse failures.
    if (name === "SyntaxError" && !stack.includes("app/javascript")) return false

    // UnhandledPromiseRejectionWarning: Timeout — generic promise timeouts.
    if (msg.includes("UnhandledPromiseRejectionWarning: Timeout")) return false
    // Safari/iOS autofill.
    if (msg.includes("_AutofillCallbackHandler")) return false
    // Algolia RetryError — unreachable hosts (network/config), not app bug.
    if (msg.includes("Unreachable hosts")) return false
    // Third-party / GTM (motosport, undefined 'page'/'marketing').
    if (msg.includes("motosport is not defined")) return false
    if (
      msg.includes("Cannot read properties of undefined") &&
      (msg.includes("reading 'page'") || msg.includes("reading 'marketing'"))
    )
      return false
    // ChunkLoadError / Loading chunk failed — old cached chunks after deploy.
    if (msg.includes("ChunkLoadError") || msg.includes("Loading chunk")) return false
    // Script error. — cross-origin script errors (browser hides detail); not actionable.
    if (msg.trim() === "Script error.") return false
    // Failed to load Stripe.js — network issue, not app bug (retried automatically).
    if (msg.includes("Failed to load Stripe.js")) return false

    // urql: empty HTTP body (204 / intermediary) after graphqlFetch normalization edge cases; non-actionable.
    if (name === "CombinedError" && msg.includes("No Content")) return false

    // Google Tag Manager: custom tags (jQuery `$`, DOM helpers like increasePoints) run without our bundle;
    // stack is gtm.js only — fix in GTM, not application code.
    if (stack.includes("googletagmanager.com/gtm.js") && !stack.includes("app/javascript")) return false

    // Generic foreign-script filter: no backtrace frame points at our bundle. Covers
    // practice-injected trackers on custom domains (e.g. "PxGrabber is not defined"
    // from web2mailbox px.min.js), browser extensions, and other third-party snippets.
    if (isForeignScriptError({ stack, backtrace: notice.backtrace })) return false

    return true
  }
})

export const wrapWithErrorBoundary = (WrappedComponent) =>
  function WithErrorBoundary(props) {
    const isTestEnv =
      typeof window !== "undefined" && (window.App?.environment === "test" || window.App?.env === "test")
    return (
      <HoneybadgerErrorBoundary honeybadger={honeybadger} ErrorComponent={isTestEnv ? TestErrorFallback : undefined}>
        <WrappedComponent {...props} />
      </HoneybadgerErrorBoundary>
    )
  }
